import { CVRMailbox, BaseMessage } from '@valve/steamvr';
import { bind } from 'bind-decorator';
import { observable, computed, action, autorun, observe } from "mobx";

import { CPairingDevice, EDevicePairingState } from './devices/pairingdevice';
import { CPairingDeviceKnuckles } from './devices/knuckles';
import { CPairingDeviceViveWand } from './devices/vivewand';
import { CPairingDeviceViveTracker } from './devices/vivetracker';
import { ESDLWindowEvent } from '@valve/steamvr';
import { string } from 'prop-types';
import { CPairingDeviceVRInkPilotEdition } from './devices/vrinkpilotedition';

export interface DongleInfo
{
	serial: string;
	firmwareVersion: string;
	connectedSerial: string;
	isPairing: boolean;
}

export interface DiscoverDonglesResponse extends BaseMessage
{
	dongles: DongleInfo[];
	expectedControllerType: string;
	isPairing: boolean;
}

export interface StartPairingResponse extends BaseMessage
{
	result: string;
	connectedSerial?: string;
}

export enum EPairingState
{
	// The initial state, when the window is hidden. No pairing operations should be happening.
	Idle,

	// This is an initial state that shows no content besides the background, because it will flash
	// briefly when the UI opens. In the event that it takes longer than a moment, "initializing..." text fades in.
	FetchingInitialDongleList,

	// This is the "turn off a controller!" screen.
	NoDonglesAvailable,

	// This is the "choose a controller type" screen.
	DongleListReady,

	// These are all sub states in the CPairingDevice
	WaitingForDeviceToPair,
	UpdatingDongleListAfterPairing, // This is a meta state that still renders the same content as the Success state.
	Success,
	TimedOut,

	// This state can happen if the user's headset becomes unplugged. It instructs them to make sure it's plugged in.
	NoDonglesDetected,

	// This state shows an generic error message and a cancel button.
	PairingFailed,
}

export class CVRPairingStore
{
	private m_mailbox = new CVRMailbox;

	@observable public dongles = new Array<DongleInfo>();
	@observable public pairingNameExpected: string;
	@observable public pairingState: EPairingState = EPairingState.Idle;
	@observable public pairingDevice: CPairingDevice;

	private windowIsShown: boolean = false;
	private activeDongle: DongleInfo;

	private pairingTimer: number;
	private waitingForDonglesResponse: boolean = false;
	private dongleLastPairedTimestamps = new Map<string, number>(); // Map of dongle serial to timestamp it was last paired. For round-robin dongle selection.

	@action public init()
	{
		this.m_mailbox.Init( "pairing_ui" ).then( () =>
		{
			this.m_mailbox.RegisterHandler( "discover_dongles_response", this.OnDiscoverDonglesResponse );
			this.m_mailbox.RegisterHandler( "pairing_result", this.OnStartPairingResponse );

			VRHTML?.RegisterForWindowEvents( this.onWindowEvent );

			this.windowIsShown = VRHTML?.IsWindowShown() ?? false;

			this.Reset();
		} )
			.catch( ( reason: any ) =>
			{
				console.log( "Failed to open pairingstore mailbox " + reason );
			} );
	}

	public getPairableDevices(): Map<String, CPairingDevice>
	{
		let pairingDevices = new Map<String, CPairingDevice>( [
			["knuckles", new CPairingDeviceKnuckles()],
			["vive_controller", new CPairingDeviceViveWand()],
			["vive_tracker", new CPairingDeviceViveTracker()],
			["vr_ink_pilot_edition", new CPairingDeviceVRInkPilotEdition()],
		] );

		return pairingDevices;
	}

	@bind private onWindowEvent( eEvent: ESDLWindowEvent )
	{
		switch ( eEvent )
		{
			case ESDLWindowEvent.Shown:
				this.windowIsShown = true;
				this.Reset();
				break;
			case ESDLWindowEvent.Hidden:
				this.windowIsShown = false;
				this.Reset();
				break;
		}
	}

	@computed
	public get HasFreeDongle()
	{
		return this.FindAvailableDongle() != null;
	}

	public selectPairingDevice( device: CPairingDevice )
	{
		this.pairingDevice = device;
		this.PairWithFreeDongle();
	}

	public FindAvailableDongle()
	{
		if ( !this.dongles )
		{
			return null;
		}

		let rSortedFreeDongles = this.dongles.filter( ( value: DongleInfo ) => value.connectedSerial == "" );
		rSortedFreeDongles.sort( ( a, b ) =>
		{
			let aTime = this.dongleLastPairedTimestamps.has( a.serial ) ? this.dongleLastPairedTimestamps.get( a.serial ) : 0;
			let bTime = this.dongleLastPairedTimestamps.has( b.serial ) ? this.dongleLastPairedTimestamps.get( b.serial ) : 0;

			return aTime - bTime;
		} );

		if ( !rSortedFreeDongles )
		{
			return null;
		}

		return rSortedFreeDongles[0];
	}

	private PromptUserToPair()
	{
		// You probably shouldn't call this function if there aren't any available dongles already.
		if ( !this.HasFreeDongle )
		{
			console.warn( "PromptUserToPair(): No available dongles." );
			this.SetState( EPairingState.NoDonglesAvailable );
			return;
		}

		if ( this.pairingNameExpected != null && this.getPairableDevices().has( this.pairingNameExpected ) )
		{
			// If we can assume a probably controller based on headset, start pairing with instructions for
			// that controller.
			PairingStore.selectPairingDevice( PairingStore.getPairableDevices().get( PairingStore.pairingNameExpected ) );
		}
		else
		{
			// Otherwise, ask the user which kind of controller they wish to pair.
			this.SetState( EPairingState.DongleListReady );
		}
	}

	@bind private OnDiscoverDonglesResponse( msg: DiscoverDonglesResponse )
	{
		this.dongles = msg.dongles;
		this.pairingNameExpected = msg.expectedControllerType;
		this.waitingForDonglesResponse = false;

		switch ( this.pairingState )
		{
			case EPairingState.FetchingInitialDongleList:
			case EPairingState.NoDonglesAvailable:
			case EPairingState.NoDonglesDetected:
				if ( this.HasFreeDongle )
				{
					this.PromptUserToPair();
				}
				else if ( this.dongles && this.dongles.length > 0 )
				{
					this.SetState( EPairingState.NoDonglesAvailable );
				}
				else
				{
					this.SetState( EPairingState.NoDonglesDetected );
				}
				break;

			case EPairingState.UpdatingDongleListAfterPairing:
				this.SetState( EPairingState.Success );
				break;
		}
	}

	@bind private OnStartPairingResponse( msg: StartPairingResponse )
	{
		if ( this.pairingState != EPairingState.WaitingForDeviceToPair )
		{
			console.log( "Pairing response when not waiting for one", msg );
		}

		switch ( msg.result )
		{
			case "timeout":
				{
					this.SetState( EPairingState.TimedOut );
				}
				break;

			case "failed_to_start":
				{
					this.SetState( EPairingState.PairingFailed );
				}
				break;

			case "canceled":
			case "success":
				{
					if ( this.pairingTimer != null )
					{
						window.clearInterval( this.pairingTimer );
						this.pairingTimer = null;
					}

					if ( this.activeDongle )
					{
						this.dongleLastPairedTimestamps.set( this.activeDongle.serial, new Date().getTime() );
					}

					this.SetState( EPairingState.UpdatingDongleListAfterPairing );
				}
				break;
		}
	}

	private SetState( eState: EPairingState )
	{
		this.pairingState = eState;

		switch ( eState )
		{
			case EPairingState.FetchingInitialDongleList:
			case EPairingState.UpdatingDongleListAfterPairing:
			case EPairingState.NoDonglesAvailable:
			case EPairingState.NoDonglesDetected:
				// In these states, we want to repeatedly search for available dongles in the background.
				this.DiscoverDongles();
				break;
			default:
				break;
		}
	}

	@bind private DiscoverDongles()
	{
		if ( this.pairingState != EPairingState.FetchingInitialDongleList &&
			this.pairingState != EPairingState.UpdatingDongleListAfterPairing &&
			this.pairingState != EPairingState.NoDonglesAvailable &&
			this.pairingState != EPairingState.NoDonglesDetected )
		{
			return;
		}

		if ( this.waitingForDonglesResponse )
		{
			return;
		}

		this.waitingForDonglesResponse = true;

		let msgDiscoverDongles =
			{
				type: "discover_dongles",
				returnAddress: this.m_mailbox.name,
			};
		console.log( "Sending DiscoverDongles" );
		this.m_mailbox.SendMessage( "lighthouse_pairing", msgDiscoverDongles );

		// NOTE: When we get a response in OnDiscoverDonglesResponse(), SetState() will be
		//       called to transition to some state (even if currently on that state), which will
		//       invoke DiscoverDongles() another time. No need to manually re-invoke it to continuously
		//       poll for dongles.
	}

	public StartPairing( dongleToPairWith: DongleInfo )
	{
		this.SetState( EPairingState.WaitingForDeviceToPair );
		this.activeDongle = dongleToPairWith;

		if ( !dongleToPairWith )
		{
			return;
		}

		let timeoutSeconds = 10;

		let msgStartPairing =
			{
				type: "start_pairing",
				returnAddress: this.m_mailbox.name,
				serial: dongleToPairWith.serial,
				timeoutSeconds,
			};

		this.pairingTimer = window.setInterval( () =>
		{
			if ( this.pairingState == EPairingState.WaitingForDeviceToPair && this.activeDongle == dongleToPairWith )
			{
				console.log( "Sending StartPairing to " + msgStartPairing.serial );
				this.m_mailbox.SendMessage( "lighthouse_pairing", msgStartPairing );
			}
			else
			{
				console.log( "Pairing has completed, clearing interval." );
				window.clearInterval( this.pairingTimer );
				this.pairingTimer = null;
			}

		}, ( timeoutSeconds - 5 ) * 1000 );

		// Kick off the pairing
		console.log( "Sending StartPairing to " + msgStartPairing.serial );
		this.m_mailbox.SendMessage( "lighthouse_pairing", msgStartPairing );
	}

	@bind public Close()
	{
		VRHTML?.CloseWindow( true );
	}

	public StopPairing( dongleToPairWith: DongleInfo )
	{
		if ( dongleToPairWith == null )
			return;

		let msgStopPairing =
			{
				type: "stop_pairing",
				returnAddress: this.m_mailbox.name,
				serial: dongleToPairWith.serial,
			};
		console.log( "Sending StopPairing to " + msgStopPairing.serial );
		this.m_mailbox.SendMessage( "lighthouse_pairing", msgStopPairing );
	}

	@bind public PairWithFreeDongle()
	{
		let availableDongle = this.FindAvailableDongle();
		if ( availableDongle != null )
		{
			this.StartPairing( availableDongle );
			return true;
		}
		else
		{
			this.SetState( EPairingState.NoDonglesAvailable );
			return false;
		}
	}

	@bind public CancelActivePairing()
	{
		this.StopPairing( this.activeDongle );
	}

	@action protected Reset()
	{
		// This will ensure we cancel any outstanding pairing requests. If none was pending,
		// this is a noop
		this.CancelActivePairing();

		this.SetState( this.windowIsShown ? EPairingState.FetchingInitialDongleList : EPairingState.Idle );

		this.pairingDevice = null;
	}

	@action public ChooseController()
	{
		this.pairingDevice = null;
	}

}

export const PairingStore = new CVRPairingStore();
